import '@modules/workspaces/host'

import { getSafeImageUrl } from '@common/helpers'

import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { getWorkspacesByUserId } from '@modules/workspaces/stores/workspaces.thunks'
import { reloadClientDataAfterWorkspaceMembershipChange } from '@modules/workspaces/stores/workspaces-membership-reload.thunks'
import {
  deleteWorkspace as deleteWorkspaceApi,
  updateWorkspaceLogo as updateWorkspaceLogoApi,
  updateWorkspaceName as updateWorkspaceNameApi,
} from '@modules/workspaces/workspaces'

import { useWorkspaceStore } from './workspace.store'
import { getNextWorkspaceIndex, resolveWorkspaceSettings } from './workspace.utils'

export function getWorkspaceSettings(): void {
  useWorkspaceStore.getState().setState({ workspace: resolveWorkspaceSettings() })
}

export async function updateWorkspaceName(name: string): Promise<void> {
  const workspaceId = useWorkspaceStore.getState().workspace?.id
  if (!workspaceId) return

  useWorkspaceStore.getState().startRequest()

  try {
    await updateWorkspaceNameApi(workspaceId, name)

    const workspacesStore = useWorkspacesStore.getState()
    const updatedWorkspaces = workspacesStore.workspaces.map((workspace) =>
      workspace.workspaceId === workspaceId ? { ...workspace, name } : workspace
    )
    const updatedCurrentWorkspace =
      workspacesStore.currentWorkspace?.workspaceId === workspaceId
        ? { ...workspacesStore.currentWorkspace, name }
        : workspacesStore.currentWorkspace

    workspacesStore.setState({
      workspaces: updatedWorkspaces,
      currentWorkspace: updatedCurrentWorkspace,
    })

    useWorkspaceStore.getState().updateState((state) => {
      if (state.workspace) {
        state.workspace.name = name
      }
    })
  } catch (err) {
    useWorkspaceStore.getState().setRequestError(err, 'Failed to update workspace name')
  } finally {
    useWorkspaceStore.getState().finishRequest()
  }
}

export async function updateWorkspaceLogo(logo: number[]): Promise<void> {
  const workspaceId = useWorkspaceStore.getState().workspace?.id
  if (!workspaceId) return

  useWorkspaceStore.getState().startRequest()

  try {
    await updateWorkspaceLogoApi(workspaceId, logo)

    await getWorkspacesByUserId({ force: true })
    const latestWorkspaces = useWorkspacesStore.getState().workspaces
    const currentWorkspace = latestWorkspaces.find((item) => item.workspaceId === workspaceId) ?? null

    if (!currentWorkspace) {
      throw new Error('Workspace refresh failed after uploading image.')
    }

    if (!getSafeImageUrl(currentWorkspace.logoUrl)) {
      throw new Error('Workspace image could not be applied. Please try again.')
    }

    useWorkspacesStore.getState().setState({
      workspaces: latestWorkspaces,
      currentWorkspace,
    })

    getWorkspaceSettings()
  } catch (err) {
    useWorkspaceStore.getState().setRequestError(err, 'Failed to update workspace logo')
  } finally {
    useWorkspaceStore.getState().finishRequest()
  }
}

export async function deleteWorkspaceByIdAndReloadClientData(workspaceId: string): Promise<void> {
  const workspacesStore = useWorkspacesStore.getState()
  const deletedWorkspaceIndex = workspacesStore.workspaces.findIndex((item) => item.workspaceId === workspaceId)
  const remainingWorkspaces = workspacesStore.workspaces.filter((item) => item.workspaceId !== workspaceId)
  const nextWorkspaceIndex = getNextWorkspaceIndex(deletedWorkspaceIndex, remainingWorkspaces.length)
  const nextWorkspace = nextWorkspaceIndex >= 0 ? remainingWorkspaces[nextWorkspaceIndex] : null

  await deleteWorkspaceApi(workspaceId)

  await reloadClientDataAfterWorkspaceMembershipChange({
    fallbackWorkspaceId: nextWorkspace?.workspaceId ?? null,
  })
}

export async function deleteWorkspace(): Promise<void> {
  const workspace = useWorkspaceStore.getState().workspace
  if (!workspace) return

  useWorkspaceStore.getState().startRequest()

  try {
    await deleteWorkspaceByIdAndReloadClientData(workspace.id)
  } catch (err) {
    useWorkspaceStore.getState().setRequestError(err, 'Failed to delete workspace')
  } finally {
    useWorkspaceStore.getState().finishRequest()
  }
}
